import React, { useState } from 'react';
import styled from 'styled-components';
import { Breakpoints, Color, Grid, Type } from '../../../app/StyleGuide';
import { SCORE_COLORS } from '../utils/scoreUtils';
import AssessmentReportCategoryItem, { AssessmentReportQuestionList } from './AssessmentReportCategoryItem';
import type { CategoryBreakdown, CategoryQuestion } from '../models/assessmentReportView';

const CategoryBreakdownHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Grid._4};
  margin-bottom: ${Grid._4};

  h3 {
    margin: 0;
  }

  @media screen and (min-width: ${Breakpoints.screen_md}) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
`;

const ScoreLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Grid._2};
`;

const ScoreLegendTitle = styled.span`
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.semibold};
  color: ${Type.Color.dark};
`;

const ScoreLegendItems = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${Grid._4};
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ScoreLegendItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${Grid._2};
  font-size: ${Type.Scale._1};
  color: ${Type.Color.medium};
`;

const ScoreLegendSwatch = styled.span<{ $color: string }>`
  width: ${Grid._4};
  height: ${Grid._4};
  border-radius: 2px;
  background: ${props => props.$color};
`;

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Grid._4};
`;

const UncategorizedSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Grid._4};
  margin-top: ${Grid._6};
`;

const UncategorizedQuestions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Grid._8};
  padding: ${Grid._5};
  border: 1px solid ${Color.Gray._20};
  border-radius: 4px;
  background: ${Color.white};
`;

export const AssessmentReportCategoryBreakdown = ({
  categories,
  uncategorizedQuestions = [],
}: {
  categories: CategoryBreakdown[];
  uncategorizedQuestions?: CategoryQuestion[];
}) => {
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  const toggleCategory = (title: string) => {
    setExpandedCategories(prev => ({ ...prev, [title]: !prev[title] }));
  };

  return (
    <div>
      <CategoryBreakdownHeader>
        <h3>Category Breakdown</h3>
        <ScoreLegend>
          <ScoreLegendTitle>Evidence of spiritual growth:</ScoreLegendTitle>
          <ScoreLegendItems>
            <ScoreLegendItem>
              <ScoreLegendSwatch $color={SCORE_COLORS.consistent} />
              Consistent 5.0–4.0
            </ScoreLegendItem>
            <ScoreLegendItem>
              <ScoreLegendSwatch $color={SCORE_COLORS.moderate} />
              Moderate 3.9–2.0
            </ScoreLegendItem>
            <ScoreLegendItem>
              <ScoreLegendSwatch $color={SCORE_COLORS.limited} />
              Limited 1.9–0.0
            </ScoreLegendItem>
          </ScoreLegendItems>
        </ScoreLegend>
      </CategoryBreakdownHeader>
      <CategoryList>
        {categories.map(category => (
          <AssessmentReportCategoryItem
            key={category.title}
            category={category}
            isExpanded={!!expandedCategories[category.title]}
            onToggle={() => toggleCategory(category.title)}
          />
        ))}
      </CategoryList>
      {uncategorizedQuestions.length > 0 && (
        <UncategorizedSection>
          <h3>Additional Questions</h3>
          <UncategorizedQuestions>
            <AssessmentReportQuestionList questions={uncategorizedQuestions} />
          </UncategorizedQuestions>
        </UncategorizedSection>
      )}
    </div>
  );
};

export default AssessmentReportCategoryBreakdown;
